import { Card, CardProps } from '@pancakeswap/uikit'
import { PageMeta } from 'components/Layout/Page'
import { LOTTERY_ARCHIVED } from 'config/constants/lottery'
import { MIGRATION_ARCHIVED } from 'config/constants/migration'
import Link from 'next/link'
import { useMemo } from 'react'
import styled from 'styled-components'
import TickerBar from 'views/TradingCompetitionV3/components/TickerBar'
import { useNow } from 'views/TradingCompetitionV3/ui'
import type { ListItem } from 'views/TokenPages/types'
import HeroSearch from './HeroSearch'
import { IdentityCards } from './IdentityCards'
import { BoardGrid, HotTokens, RecentLocks } from './home/Board'
import { useBoardCompetitions, useHotTokens, useRecentLocks, useToolLines } from './home/hooks'
import { LivePanel } from './home/LivePanel'
import { ProjectPath } from './home/Path'
import { LaunchStrip, ToolTiles } from './home/Tools'
import { DISPLAY, Fluent, HUES, ICE, LINE, MONO, MUTED, NIGHT, SNOW, SNOW_2 } from './home/ui'

/**
 * Home (Bez 2026-10-09: "more attractive and more functional, not just a landing"). The home is where a BNB Chain project builds and grows:
 * the first screen is the four tools as big tiles with their live price badges (Swap, Lock, Compete, Token page), the CAD1 launcher shown as
 * "in testing" only, the token search and what is live now; under it the proof the place is alive (hot tokens, newest locks), the path a
 * project takes, the identity deck (Bez's six-husky mock, kept) and every other product (nothing removed). The page is always night.
 * No transaction happens here: every tile and row is a link to the page that owns the flow.
 */

/** Kept for the Lock Tools create pages, which import this card frame from here. */
export const BodyWrapper = styled(Card)`
  border-radius: ${({ theme }) => theme.radii.cardLarge};
  max-width: 380px;
  width: 100%;
  z-index: 1;
`

export function AppBody({ children, ...cardProps }: { children: React.ReactNode } & CardProps) {
  return <BodyWrapper {...cardProps}>{children}</BodyWrapper>
}

const Page = styled.div`
  position: relative;
  min-height: calc(100vh - 56px);
  overflow-x: clip;
  color: ${SNOW};
  background: radial-gradient(70% 50% at 0% 0%, rgba(33, 0, 255, 0.22) 0%, rgba(33, 0, 255, 0) 70%),
    radial-gradient(60% 40% at 100% 8%, rgba(126, 0, 254, 0.2) 0%, rgba(126, 0, 254, 0) 70%),
    radial-gradient(50% 30% at 50% 60%, rgba(127, 212, 232, 0.06) 0%, rgba(127, 212, 232, 0) 70%), ${NIGHT};
`
const Wrap = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
  ${({ theme }) => theme.mediaQueries.md} {
    padding: 0 24px;
  }
`
/** The live activity bar under the site header; its 38 px are kept from the first paint. */
const Live = styled.div`
  padding-top: 10px;
  ${({ theme }) => theme.mediaQueries.md} {
    padding-top: 12px;
  }
`
const Hero = styled.section`
  position: relative;
  z-index: 2; /* the search results open over the sections under the hero */
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-areas: 'head' 'tools' 'search' 'live' 'launch';
  gap: 14px;
  padding: 10px 0 8px;
  ${({ theme }) => theme.mediaQueries.md} {
    gap: 18px;
    padding: 24px 0 8px;
  }
  /* wide: headline and search on the left, "Live now" on the right, the tools in one row under both */
  ${({ theme }) => theme.mediaQueries.lg} {
    grid-template-columns: minmax(0, 1.12fr) minmax(0, 0.88fr);
    grid-template-areas: 'head live' 'search live' 'tools tools' 'launch launch';
    column-gap: 28px;
    row-gap: 18px;
    align-items: start;
  }
  > [data-area='head'] {
    grid-area: head;
  }
  > [data-area='tools'] {
    grid-area: tools;
  }
  > [data-area='launch'] {
    grid-area: launch;
  }
  > [data-area='search'] {
    grid-area: search;
    min-width: 0;
  }
  > [data-area='search'] > div {
    max-width: none;
    margin-top: 0;
  }
  > [data-area='live'] {
    grid-area: live;
  }
`
const Eyebrow = styled.div`
  font-family: ${MONO};
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${ICE};
  margin-bottom: 8px;
`
const Title = styled.h1`
  margin: 0;
  font-family: ${DISPLAY};
  font-weight: 900;
  font-size: 28px;
  line-height: 32px;
  letter-spacing: -0.02em;
  color: ${SNOW};
  text-wrap: balance;
  em {
    font-style: normal;
    color: ${ICE};
  }
  ${({ theme }) => theme.mediaQueries.sm} {
    font-size: 36px;
    line-height: 42px;
  }
  ${({ theme }) => theme.mediaQueries.lg} {
    font-size: 46px;
    line-height: 52px;
  }
`
const Lede = styled.p`
  display: none;
  margin: 12px 0 0;
  max-width: 560px;
  font-size: 16px;
  line-height: 24px;
  color: ${SNOW_2};
  ${({ theme }) => theme.mediaQueries.sm} {
    display: block;
  }
`
const Section = styled.section`
  padding: 28px 0 0;
  ${({ theme }) => theme.mediaQueries.md} {
    padding: 44px 0 0;
  }
`
const SectionHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  h2 {
    margin: 0;
    font-family: ${DISPLAY};
    font-weight: 800;
    font-size: 22px;
    line-height: 28px;
    letter-spacing: -0.01em;
    color: ${SNOW};
  }
  p {
    margin: 2px 0 0;
    font-size: 13px;
    line-height: 18px;
    color: ${MUTED};
  }
  ${({ theme }) => theme.mediaQueries.md} {
    h2 {
      font-size: 26px;
      line-height: 32px;
    }
  }
`
/* identity deck on the left, the other products on the right (stacked on a phone) */
const Duo = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 16px;
  padding-bottom: 48px;
  ${({ theme }) => theme.mediaQueries.lg} {
    grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
    gap: 20px;
    align-items: start;
  }
`
const IdentityBox = styled.div`
  position: relative;
  overflow: hidden;
  padding: 24px 12px 20px;
  border-radius: 22px;
  border: 1px solid ${LINE};
  background: radial-gradient(70% 60% at 50% 30%, rgba(127, 212, 232, 0.14) 0%, rgba(127, 212, 232, 0) 70%),
    radial-gradient(60% 50% at 50% 100%, rgba(126, 0, 254, 0.18) 0%, rgba(126, 0, 254, 0) 70%), #0a0e22;
  box-shadow: inset 0 1px 0 rgba(223, 233, 255, 0.1), 0 16px 40px rgba(0, 0, 0, 0.4);
`
/* one column on a phone, two side by side on a tablet, one again beside the identity deck (the two columns end on one line) */
const MoreGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 10px;
  ${({ theme }) => theme.mediaQueries.sm} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  ${({ theme }) => theme.mediaQueries.lg} {
    grid-template-columns: minmax(0, 1fr);
  }
`
const MoreRow = styled(Link)`
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) auto;
  align-items: center;
  column-gap: 12px;
  min-height: 72px;
  padding: 12px 14px;
  border-radius: 18px;
  color: ${SNOW};
  background: linear-gradient(180deg, rgba(159, 182, 255, 0.06) 0%, rgba(159, 182, 255, 0) 60%), #0b1028;
  border: 1px solid ${LINE};
  box-shadow: inset 0 1px 0 rgba(223, 233, 255, 0.08), 0 8px 22px rgba(0, 0, 0, 0.35);
  transition: border-color 150ms ease;
  &:hover {
    border-color: rgba(${HUES.ice.rgb}, 0.7);
  }
  &:focus-visible {
    outline: 2px solid ${ICE};
    outline-offset: 2px;
  }
  strong {
    display: block;
    font-family: ${DISPLAY};
    font-weight: 800;
    font-size: 16px;
    line-height: 20px;
  }
  span.d {
    display: block;
    font-size: 13px;
    line-height: 18px;
    color: ${SNOW_2};
  }
  .arrow {
    font-weight: 800;
    color: ${MUTED};
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

type Entry = { title: string; desc: string; href: string; icon: string }

export default function ShowCase() {
  const now = useNow(30_000)
  const tools = useToolLines()
  const comps = useBoardCompetitions(now)
  const hot = useHotTokens(5)
  const locks = useRecentLocks(4)
  // symbols and logos of competition tokens come from the token list when it has them (no extra read)
  const tokenMap = useMemo(() => Object.fromEntries(hot.all.map((t) => [t.address.toLowerCase(), t])) as Record<string, ListItem>, [hot.all])

  const more: Entry[] = [
    { title: 'Liquidity', desc: 'Add or remove liquidity, earn a share of the fees.', href: '/liquidity', icon: 'moneybag' },
    { title: 'Farms & Pools', desc: 'Stake LP tokens or CADINU for CBON rewards.', href: '/farms', icon: 'gem' },
    { title: 'Prediction Markets', desc: 'Trade the outcome of real-world events.', href: '/prediction-markets', icon: 'crystal' },
    { title: 'Markets', desc: 'Pairs, tokens and volume on CADINUdex.', href: '/info', icon: 'chart' },
    { title: 'NFT Referral', desc: 'CADINU NFTs and referral rewards.', href: '/u?buy=1#nfts', icon: 'gift' },
    { title: 'Governance', desc: 'Create proposals or cast your vote.', href: '/governance', icon: 'ballot' },
    // 2026-10-01: lottery parked (LOTTERY_ARCHIVED=1): no home row; /lottery keeps the old-prize claim flow
    ...(LOTTERY_ARCHIVED ? [] : [{ title: 'Lottery', desc: 'Win CADINU rewards in the lottery draws.', href: '/lottery', icon: 'party' }]),
    ...(MIGRATION_ARCHIVED ? [] : [{ title: 'Migration', desc: 'Migrate your tokens to the current contract.', href: '/migration', icon: 'key' }]),
    // CADINU Games is reached from the cadinu.io landing, not from the DEX (Bez, 2026-10-02); Click to Win left on 2026-10-08 (archived)
  ]

  return (
    <>
      <PageMeta />
      <Page data-testid="home-page">
        <Wrap>
          <Live data-testid="home-live">
            <TickerBar force reserve />
          </Live>
          <Hero aria-labelledby="home-title">
            <div data-area="head">
              <Eyebrow>CADINUdex · BNB Chain</Eyebrow>
              <Title id="home-title">
                Your token&apos;s <em>home</em> on BNB Chain.
              </Title>
              <Lede>Swap at the best price, lock liquidity, run trading competitions and give your token a page. The prices on the tools are read live.</Lede>
            </div>
            <div data-area="tools">
              <ToolTiles lines={tools} />
            </div>
            <div data-area="launch">
              <LaunchStrip />
            </div>
            <div data-area="search">
              <HeroSearch />
            </div>
            <div data-area="live">
              <LivePanel {...comps} tokens={tokenMap} now={now} />
            </div>
          </Hero>

          <Section aria-label="Live on CADINUdex">
            <BoardGrid>
              <HotTokens rows={hot.rows} loading={hot.loading} failed={hot.failed} />
              <RecentLocks rows={locks.rows} totals={locks.totals} loading={locks.loading} failed={locks.failed} now={now} />
            </BoardGrid>
          </Section>

          <Section aria-labelledby="home-path-title">
            <SectionHead>
              <div>
                <h2 id="home-path-title">How a project uses CADINU</h2>
                <p>Four steps, one place. The prices are the same live reads as the tools above.</p>
              </div>
            </SectionHead>
            <ProjectPath lock={tools.lock.badge} compete={tools.compete.badge} launch={tools.launch.badge} />
          </Section>

          <Section aria-labelledby="home-more">
            <Duo>
              <IdentityBox>
                <IdentityCards />
              </IdentityBox>
              <div>
                <SectionHead>
                  <h2 id="home-more">More on CADINU</h2>
                </SectionHead>
                <MoreGrid>
                  {more.map((m) => (
                    <MoreRow key={m.href} href={m.href}>
                      <Fluent name={m.icon} size={36} />
                      <span style={{ minWidth: 0 }}>
                        <strong>{m.title}</strong>
                        <span className="d">{m.desc}</span>
                      </span>
                      <span className="arrow" aria-hidden>
                        →
                      </span>
                    </MoreRow>
                  ))}
                </MoreGrid>
              </div>
            </Duo>
          </Section>
        </Wrap>
      </Page>
    </>
  )
}
